/**
 * PATH: apps/qa/src/pages/record-detail/ParticipantsModal.tsx
 * PURPOSE: Manage per-job QA role grants from the inspection detail page.
 * WHY: The inspection IS the job-specific QA gate (set up from quotes/specs) —
 *      so the team who will execute it gets granted here, not in Django Admin.
 *      A QA lead adds a worker by email, picks a role, optionally hold-point
 *      signing; can change roles or revoke. Backend enforces authority
 *      (org qa:admin or qa_manager/pm on the job) and same-org membership.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { participantApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { JobParticipant, RoleOnJob } from '@/types/qa'
import { ROLE_OPTIONS, roleLabel, extractApiError } from './participantRoles'
import { Users, X, Loader2, UserPlus, Trash2, PenTool } from 'lucide-react'

interface ParticipantsModalProps {
  jobId: number
  jobName?: string
  onClose: () => void
}

export function ParticipantsModal({ jobId, jobName, onClose }: ParticipantsModalProps) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<RoleOnJob>('installer')
  const [canSign, setCanSign] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const queryKey = ['qa-participants', jobId]
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => participantApi.getParticipants({ job_id: jobId }),
  })
  const participants = data?.results ?? []

  const refresh = () => queryClient.invalidateQueries({ queryKey })

  const addMutation = useMutation({
    mutationFn: () => participantApi.addParticipant({
      job: jobId,
      user_email: email.trim(),
      role_on_job: role,
      can_sign_hold_points: canSign,
    }),
    onSuccess: () => { setEmail(''); setCanSign(false); setError(null); refresh() },
    onError: (err) => setError(extractApiError(err, 'Failed to add participant')),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }: { id: string; role_on_job?: RoleOnJob; can_sign_hold_points?: boolean }) =>
      participantApi.updateParticipant(id, payload),
    onSuccess: () => { setError(null); refresh() },
    onError: (err) => setError(extractApiError(err, 'Failed to update participant')),
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => participantApi.removeParticipant(id),
    onSuccess: () => { setError(null); refresh() },
    onError: (err) => setError(extractApiError(err, 'Failed to remove participant')),
  })

  const busy = addMutation.isPending || updateMutation.isPending || removeMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-lg shadow-xl border border-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Job Team
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Who can work QA on {jobName ? <span className="font-medium text-foreground">{jobName}</span> : 'this job'} — grants apply to every inspection on the job.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>
        )}

        {/* Add form */}
        <form
          className="mb-6 p-3 rounded-lg border border-border bg-muted/30 space-y-3"
          onSubmit={(e) => { e.preventDefault(); if (email.trim() && !busy) addMutation.mutate() }}
        >
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="worker@company.com"
              className="flex-1 px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as RoleOnJob)}
              className="px-2 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={canSign} onChange={(e) => setCanSign(e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary focus:ring-primary/20" />
              Can sign hold points
            </label>
            <button type="submit" disabled={!email.trim() || busy}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {addMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {ROLE_OPTIONS.find(r => r.value === role)?.hint}. Adding someone already on the job updates their role.
          </p>
        </form>

        {/* Participant list */}
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : participants.length === 0 ? (
          <div className="text-center py-6">
            <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No one has been granted QA access on this job yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {participants.map((p: JobParticipant) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border border-border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{p.user_name?.trim() || p.user_email_display || 'Unknown user'}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.user_email_display}</p>
                </div>
                {p.can_sign_hold_points && (
                  <span title="Can sign hold points" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                    <PenTool className="w-3 h-3" /> HP
                  </span>
                )}
                <select
                  value={p.role_on_job}
                  disabled={busy}
                  onChange={(e) => updateMutation.mutate({ id: p.id, role_on_job: e.target.value as RoleOnJob })}
                  className="px-2 py-1 rounded-lg border border-input bg-background text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                  aria-label={`Role for ${p.user_email_display || p.id}`}
                >
                  {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  {/* Preserve unknown/legacy roles rather than mislabelling them */}
                  {!ROLE_OPTIONS.some(r => r.value === p.role_on_job) && (
                    <option value={p.role_on_job}>{roleLabel(p.role_on_job)}</option>
                  )}
                </select>
                <button
                  onClick={() => removeMutation.mutate(p.id)}
                  disabled={busy}
                  title="Revoke QA access"
                  className={cn('p-1.5 rounded-lg transition-colors text-muted-foreground hover:text-destructive hover:bg-destructive/10', busy && 'opacity-50')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Done</button>
        </div>
      </div>
    </div>
  )
}
